import React, { useState, useCallback, useEffect, useRef } from 'react';
import styles from './PDFViewer.module.css';

// debounce 함수 정의
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

interface PDFViewerProps {
  url: string;
  title?: string;
  onError?: (error: Error) => void;
  onDownload?: () => void;
  onFullscreen?: () => void;
  showControls?: boolean;
  isFullscreen?: boolean;
}

// PDF.js 타입 정의
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  url, 
  title = "PDF 문서", 
  onError,
  onDownload,
  onFullscreen,
  showControls = true,
  isFullscreen = false,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [scale] = useState<number>(1.0);
  const [pdfJsLoaded, setPdfJsLoaded] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [isRendering, setIsRendering] = useState<boolean>(false);
  const [showTwoPages, setShowTwoPages] = useState<boolean>(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvas2Ref = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const lastRenderedPageRef = useRef<number>(0);
  const lastRenderedScaleRef = useRef<number>(1.0);
  const lastRenderedModeRef = useRef<boolean>(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // PDF.js 라이브러리 로드
  const loadPdfJs = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (window.pdfjsLib) {
        setPdfJsLoaded(true);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        setPdfJsLoaded(true);
        resolve();
      };
      script.onerror = () => {
        reject(new Error('PDF.js 로드 실패'));
      };
      document.head.appendChild(script);
    });
  }, []);

  // PDF를 Blob으로 다운로드하고 URL 생성 (재시도 로직 포함)
  const downloadPdfAsBlob = useCallback(async (pdfUrl: string, retryAttempt: number = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryAttempt), 5000); // 지수 백오프

    try {
      setLoading(true);
      setError(null);
      setRetryCount(retryAttempt);

      const response = await fetch(pdfUrl, { 
        method: 'GET', 
        mode: 'cors', 
        cache: 'no-cache',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('빈 PDF 파일');
      }
      
      // Blob 타입 검증
      if (!blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
        console.warn('PDF 타입이 아닐 수 있습니다:', blob.type);
      }
      
      const newBlobUrl = URL.createObjectURL(blob);
      setBlobUrl(newBlobUrl);
      setPdfBlob(blob);
      setRetryCount(0); // 성공 시 재시도 카운트 리셋
      
    } catch (error) {
      console.error(`PDF 다운로드 실패 (시도 ${retryAttempt + 1}/${maxRetries + 1}):`, error);
      
      if (retryAttempt < maxRetries) {
        // 재시도
        setTimeout(() => {
          downloadPdfAsBlob(pdfUrl, retryAttempt + 1);
        }, retryDelay);
      } else {
        setError('PDF를 로드할 수 없습니다. 네트워크를 확인해주세요.');
        setLoading(false);
        onError?.(new Error('PDF 로드 실패'));
      }
    }
  }, [onError]);

  // PDF.js로 PDF 로드 (Blob 데이터 직접 전달, 재시도 로직 포함)
  const loadPdfWithPdfJs = useCallback(async (blob: Blob, retryAttempt: number = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryAttempt), 5000);

    if (!window.pdfjsLib) {
      setError('PDF.js가 로드되지 않았습니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Blob을 ArrayBuffer로 변환
      const arrayBuffer = await blob.arrayBuffer();
      
      // PDF.js 설정 (ArrayBuffer 사용)
      const loadingTask = window.pdfjsLib.getDocument({
        data: arrayBuffer,
        disableAutoFetch: true,
        disableStream: true,
        verbosity: 0 // 로그 레벨 낮춤
      });

      const pdf = await loadingTask.promise;
      
      if (!pdf || pdf.numPages === 0) {
        throw new Error('유효하지 않은 PDF 파일입니다.');
      }

      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
      setLoading(false);
      
    } catch (error) {
      console.error(`PDF.js 로드 실패 (시도 ${retryAttempt + 1}/${maxRetries + 1}):`, error);
      
      if (retryAttempt < maxRetries) {
        // 재시도
        setTimeout(() => {
          loadPdfWithPdfJs(blob, retryAttempt + 1);
        }, retryDelay);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'PDF를 로드할 수 없습니다.';
        setError(errorMessage);
        setLoading(false);
        onError?.(new Error(`PDF 로드 실패: ${errorMessage}`));
      }
    }
  }, [onError]);

  // 화면 크기 감지 및 랜드스케이프 모드 판단
  const checkLandscapeMode = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    // 랜드스케이프 모드 판단 (가로가 세로의 1.5배 이상)
    const landscape = width > height * 1.5;
    
    // 2페이지 보기 활성화 조건: 랜드스케이프 모드이고 충분히 넓을 때
    const shouldShowTwoPages = landscape && width > 1000;
    setShowTwoPages(shouldShowTwoPages);
  }, []);

  // PDF 페이지 렌더링 (재시도 로직 포함)
  const renderPdfPage = useCallback(async (pageNumber: number, retryAttempt: number = 0, forceRender: boolean = false) => {
    console.log('renderPdfPage', pageNumber, retryAttempt, 'forceRender:', forceRender);
    const maxRetries = 2; // 재시도 횟수 줄임
    const retryDelay = Math.min(300 * Math.pow(2, retryAttempt), 1000); // 재시도 지연 시간 줄임

    if (!pdfDocument || !canvasRef.current) {
      if (retryAttempt < maxRetries) {
        setTimeout(() => {
          renderPdfPage(pageNumber, retryAttempt + 1, forceRender);
        }, retryDelay);
      }
      return;
    }

    // 강제 렌더링이 아닌 경우에만 스킵 체크
    if (!forceRender) {
      // 이미 같은 페이지, 스케일, 모드로 렌더링되었으면 스킵
      if (lastRenderedPageRef.current === pageNumber && 
          lastRenderedScaleRef.current === scale && 
          lastRenderedModeRef.current === false) {
        console.log('이미 렌더링된 페이지, 스케일, 모드, 스킵');
        return;
      }

      if (isRendering) {
        return;
      }
    }

    try {
      setIsRendering(true);

      const page = await pdfDocument.getPage(pageNumber);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (!context) {
        throw new Error('Canvas context를 가져올 수 없습니다.');
      }

      // 전체화면 모드일 때 자동 스케일 계산
      let finalScale = scale;
      if (isFullscreen && scale === 0) {
        const container = canvas.parentElement;
        if (container) {
          const containerHeight = container.clientHeight - 100; // 컨트롤 공간 확보
          const containerWidth = container.clientWidth - 100;
          const pageViewport = page.getViewport({ scale: 1 });
          
          const scaleByHeight = containerHeight / pageViewport.height;
          const scaleByWidth = containerWidth / pageViewport.width;
          finalScale = Math.min(scaleByHeight, scaleByWidth, 2); // 최대 2배까지
        }
      }

      const viewport = page.getViewport({ scale: finalScale });
      
      // Canvas 크기 설정 (디바이스 픽셀 비율 고려)
      const devicePixelRatio = window.devicePixelRatio || 1;
      const newWidth = viewport.width * devicePixelRatio;
      const newHeight = viewport.height * devicePixelRatio;
      
      // 크기가 실제로 변경되었을 때만 Canvas 크기 조정
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth;
        canvas.height = newHeight;
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';
      }

      // Canvas 완전 리셋 (뒤집힘 방지)
      context.save(); // 현재 상태 저장
      context.setTransform(1, 0, 0, 1, 0, 0); // Transform 리셋
      context.clearRect(0, 0, canvas.width, canvas.height); // Canvas 완전 정리
      context.restore(); // 저장된 상태 복원
      
      // 새로운 스케일 적용
      context.scale(devicePixelRatio, devicePixelRatio);

      // 깜빡임 방지를 위해 배경을 먼저 설정
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, viewport.width, viewport.height);

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      // 렌더링 작업 생성 및 실행
      const renderTask = page.render(renderContext);
      await renderTask.promise;
      
      // 렌더링 완료 후 ref 업데이트
      lastRenderedPageRef.current = pageNumber;
      lastRenderedScaleRef.current = scale;
      lastRenderedModeRef.current = false;
      
      setIsRendering(false);
      
    } catch (error) {
      console.error(`PDF 렌더링 오류 (시도 ${retryAttempt + 1}/${maxRetries + 1}):`, error);
      setIsRendering(false);
      
      if (retryAttempt < maxRetries) {
        setTimeout(() => {
          renderPdfPage(pageNumber, retryAttempt + 1);
        }, retryDelay);
      } else {
        setError('PDF 페이지를 렌더링할 수 없습니다.');
      }
    }
  }, [pdfDocument, scale, isRendering, isFullscreen]);

  // 2페이지 렌더링 (재시도 로직 포함)
  const renderTwoPages = useCallback(async (leftPageNumber: number, rightPageNumber: number, retryAttempt: number = 0, forceRender: boolean = false) => {
    console.log('renderTwoPages', leftPageNumber, rightPageNumber, retryAttempt, 'forceRender:', forceRender);
    const maxRetries = 2;
    const retryDelay = Math.min(300 * Math.pow(2, retryAttempt), 1000);

    if (!pdfDocument || !canvasRef.current || !canvas2Ref.current) {
      if (retryAttempt < maxRetries) {
        setTimeout(() => {
          renderTwoPages(leftPageNumber, rightPageNumber, retryAttempt + 1, forceRender);
        }, retryDelay);
      }
      return;
    }

    // 강제 렌더링이 아닌 경우에만 스킵 체크
    if (!forceRender) {
      // 이미 같은 페이지 조합, 스케일, 모드로 렌더링되었으면 스킵
      if (lastRenderedPageRef.current === leftPageNumber && 
          lastRenderedScaleRef.current === scale && 
          lastRenderedModeRef.current === true) {
        return;
      }

      if (isRendering) {
        return;
      }
    }

    try {
      setIsRendering(true);

      // 왼쪽 페이지 렌더링
      const leftPage = await pdfDocument.getPage(leftPageNumber);
      const leftCanvas = canvasRef.current;
      const leftContext = leftCanvas.getContext('2d');
      
      if (!leftContext) {
        throw new Error('Left canvas context를 가져올 수 없습니다.');
      }

      // 전체화면 모드일 때 자동 스케일 계산
      let finalScale = scale;
      if (isFullscreen && scale === 0) {
        const container = leftCanvas.parentElement;
        if (container) {
          const containerHeight = container.clientHeight - 100; // 컨트롤 공간 확보
          const containerWidth = container.clientWidth - 200; // 2페이지 모드이므로 더 넓은 공간 확보
          const pageViewport = leftPage.getViewport({ scale: 1 });
          
          const scaleByHeight = containerHeight / pageViewport.height;
          const scaleByWidth = containerWidth / pageViewport.width;
          finalScale = Math.min(scaleByHeight, scaleByWidth, 2); // 최대 2배까지
        }
      }

      const leftViewport = leftPage.getViewport({ scale: finalScale });
      
      const devicePixelRatio = window.devicePixelRatio || 1;
      const leftWidth = leftViewport.width * devicePixelRatio;
      const leftHeight = leftViewport.height * devicePixelRatio;
      
      if (leftCanvas.width !== leftWidth || leftCanvas.height !== leftHeight) {
        leftCanvas.width = leftWidth;
        leftCanvas.height = leftHeight;
        leftCanvas.style.width = leftViewport.width + 'px';
        leftCanvas.style.height = leftViewport.height + 'px';
      }

      // Canvas 완전 리셋 (뒤집힘 방지)
      leftContext.save(); // 현재 상태 저장
      leftContext.setTransform(1, 0, 0, 1, 0, 0); // Transform 리셋
      leftContext.clearRect(0, 0, leftCanvas.width, leftCanvas.height); // Canvas 완전 정리
      leftContext.restore(); // 저장된 상태 복원
      
      // 새로운 스케일 적용
      leftContext.scale(devicePixelRatio, devicePixelRatio);

      // 깜빡임 방지를 위해 배경을 먼저 설정
      leftContext.fillStyle = '#ffffff';
      leftContext.fillRect(0, 0, leftViewport.width, leftViewport.height);

      const leftRenderContext = {
        canvasContext: leftContext,
        viewport: leftViewport
      };

      const leftRenderTask = leftPage.render(leftRenderContext);
      await leftRenderTask.promise;

      // 오른쪽 페이지 렌더링 (있는 경우)
      if (rightPageNumber <= totalPages) {
        const rightPage = await pdfDocument.getPage(rightPageNumber);
        const rightCanvas = canvas2Ref.current;
        const rightContext = rightCanvas.getContext('2d');
        
        if (!rightContext) {
          throw new Error('Right canvas context를 가져올 수 없습니다.');
        }

        const rightViewport = rightPage.getViewport({ scale: finalScale });
        
        const rightWidth = rightViewport.width * devicePixelRatio;
        const rightHeight = rightViewport.height * devicePixelRatio;
        
        if (rightCanvas.width !== rightWidth || rightCanvas.height !== rightHeight) {
          rightCanvas.width = rightWidth;
          rightCanvas.height = rightHeight;
          rightCanvas.style.width = rightViewport.width + 'px';
          rightCanvas.style.height = rightViewport.height + 'px';
        }

        // Canvas 완전 리셋 (뒤집힘 방지)
        rightContext.save(); // 현재 상태 저장
        rightContext.setTransform(1, 0, 0, 1, 0, 0); // Transform 리셋
        rightContext.clearRect(0, 0, rightCanvas.width, rightCanvas.height); // Canvas 완전 정리
        rightContext.restore(); // 저장된 상태 복원
        
        // 새로운 스케일 적용
        rightContext.scale(devicePixelRatio, devicePixelRatio);

        // 깜빡임 방지를 위해 배경을 먼저 설정
        rightContext.fillStyle = '#ffffff';
        rightContext.fillRect(0, 0, rightViewport.width, rightViewport.height);

        const rightRenderContext = {
          canvasContext: rightContext,
          viewport: rightViewport
        };

        const rightRenderTask = rightPage.render(rightRenderContext);
        await rightRenderTask.promise;
      }

      // 렌더링된 페이지 조합으로 업데이트
      lastRenderedPageRef.current = leftPageNumber;
      lastRenderedScaleRef.current = scale;
      lastRenderedModeRef.current = true;
      
      setIsRendering(false);
      
    } catch (error) {
      console.error(`2페이지 렌더링 오류 (시도 ${retryAttempt + 1}/${maxRetries + 1}):`, error);
      setIsRendering(false);
      
      if (retryAttempt < maxRetries) {
        setTimeout(() => {
          renderTwoPages(leftPageNumber, rightPageNumber, retryAttempt + 1);
        }, retryDelay);
      } else {
        setError('PDF 페이지를 렌더링할 수 없습니다.');
      }
    }
  }, [pdfDocument, scale, isRendering, totalPages, isFullscreen]);

  // 페이지 변경 시 렌더링 (디바운싱 적용)
  useEffect(() => {
    if (!pdfDocument || !currentPage) return;

    // 이전 타임아웃 정리
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    renderTimeoutRef.current = setTimeout(() => {
      if (!isRendering) {
        if (showTwoPages) {
          // 2페이지 모드: 스프레드 형태 (홀수 페이지부터 시작)
          let leftPage, rightPage;
          
          // 홀수 페이지부터 시작하여 2페이지씩 이동
          if (currentPage % 2 === 1) {
            // 홀수 페이지: 왼쪽에 현재 페이지, 오른쪽에 다음 페이지
            leftPage = currentPage;
            rightPage = currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;
          } else {
            // 짝수 페이지를 홀수로 조정 (이전 홀수 페이지로)
            const adjustedPage = currentPage - 1;
            leftPage = adjustedPage;
            rightPage = adjustedPage + 1 <= totalPages ? adjustedPage + 1 : adjustedPage;
          }
          
          renderTwoPages(leftPage, rightPage);
        } else {
          // 1페이지 모드
          renderPdfPage(currentPage);
        }
      }
    }, 150); // 디바운싱 시간 증가

    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [pdfDocument, currentPage, isRendering, renderPdfPage, renderTwoPages, showTwoPages, totalPages, isFullscreen]);

  // 전체화면 모드에서 창 크기 변경 감지
  useEffect(() => {
    if (!isFullscreen) return;

    const handleResize = debounce(() => {
      if (pdfDocument && currentPage && !isRendering) {
        // 전체화면 모드에서 창 크기 변경 시 자동으로 다시 렌더링
        if (showTwoPages) {
          // 2페이지 모드: 스프레드 형태 (홀수 페이지부터 시작)
          let leftPage, rightPage;
          
          // 홀수 페이지부터 시작하여 2페이지씩 이동
          if (currentPage % 2 === 1) {
            // 홀수 페이지: 왼쪽에 현재 페이지, 오른쪽에 다음 페이지
            leftPage = currentPage;
            rightPage = currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;
          } else {
            // 짝수 페이지를 홀수로 조정 (이전 홀수 페이지로)
            const adjustedPage = currentPage - 1;
            leftPage = adjustedPage;
            rightPage = adjustedPage + 1 <= totalPages ? adjustedPage + 1 : adjustedPage;
          }
          
          renderTwoPages(leftPage, rightPage, 0, true); // forceRender = true
        } else {
          // 1페이지 모드
          renderPdfPage(currentPage, 0, true); // forceRender = true
        }
      }
    }, 300);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isFullscreen, pdfDocument, currentPage, isRendering, renderPdfPage, renderTwoPages, showTwoPages, totalPages]);

  // URL이 변경되면 Blob 다운로드
  useEffect(() => {
    if (url) {
      downloadPdfAsBlob(url);
    }
  }, [url, downloadPdfAsBlob]);

  // Blob이 생성되면 PDF.js로 로드
  useEffect(() => {
    if (pdfBlob && pdfJsLoaded) {
      loadPdfWithPdfJs(pdfBlob);
    }
  }, [pdfBlob, pdfJsLoaded, loadPdfWithPdfJs]);

  // PDF.js 로드
  useEffect(() => {
    loadPdfJs().catch((error) => {
      setError('PDF.js를 로드할 수 없습니다.');
      setLoading(false);
    });
  }, [loadPdfJs]);

  // ResizeObserver 설정 (브라우저 크기 조절 감지)
  useEffect(() => {
    if (!containerRef.current || !pdfDocument) return;

    let resizeTimeout: NodeJS.Timeout;
    let lastSize = { width: 0, height: 0 };
    
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      
      const { width, height } = entry.contentRect;
      
      // 크기가 실제로 변경되었을 때만 렌더링
      if (Math.abs(width - lastSize.width) > 10 || Math.abs(height - lastSize.height) > 10) {
        lastSize = { width, height };
        
        // 랜드스케이프 모드 체크
        checkLandscapeMode();
        
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (pdfDocument && currentPage && !isRendering) {
            // 랜드스케이프 모드 재체크
            checkLandscapeMode();
            
            // 약간의 지연 후 렌더링 (모드 변경 완료 후)
            setTimeout(() => {
              if (showTwoPages) {
                // 2페이지 모드: 스프레드 형태 (홀수 페이지부터 시작)
                let leftPage, rightPage;
                
                // 홀수 페이지부터 시작하여 2페이지씩 이동
                if (currentPage % 2 === 1) {
                  // 홀수 페이지: 왼쪽에 현재 페이지, 오른쪽에 다음 페이지
                  leftPage = currentPage;
                  rightPage = currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;
                } else {
                  // 짝수 페이지를 홀수로 조정 (이전 홀수 페이지로)
                  const adjustedPage = currentPage - 1;
                  leftPage = adjustedPage;
                  rightPage = adjustedPage + 1 <= totalPages ? adjustedPage + 1 : adjustedPage;
                }
                
                renderTwoPages(leftPage, rightPage);
              } else {
                renderPdfPage(currentPage);
              }
            }, 100);
          }
        }, 300); // 디바운싱 시간 조정
      }
    });

    resizeObserver.observe(containerRef.current);
    resizeObserverRef.current = resizeObserver;

    return () => {
      clearTimeout(resizeTimeout);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [pdfDocument, currentPage, isRendering, renderPdfPage, renderTwoPages, showTwoPages, totalPages, checkLandscapeMode]);

  // 이전 페이지
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      if (showTwoPages) {
        // 2페이지 모드에서는 2페이지씩 이동
        setCurrentPage(Math.max(1, currentPage - 2));
      } else {
        // 1페이지 모드에서는 1페이지씩 이동
        setCurrentPage(currentPage - 1);
      }
    }
  };

  // 다음 페이지
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      if (showTwoPages) {
        // 2페이지 모드에서는 2페이지씩 이동
        setCurrentPage(Math.min(totalPages, currentPage + 2));
      } else {
        // 1페이지 모드에서는 1페이지씩 이동
        setCurrentPage(currentPage + 1);
      }
    }
  };

  // 페이지 이동
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [blobUrl]);


  // 로딩 상태
  if (loading) {
    return (
      <div className={styles.pdfViewer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>PDF를 로드하는 중...</p>
          {retryCount > 0 && (
            <p className={styles.retryInfo}>
              재시도 중... ({retryCount}/3)
            </p>
          )}
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className={styles.pdfViewer}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h3>PDF 로드 실패</h3>
          <p>{error}</p>
          <div className={styles.errorActions}>
            <button 
              onClick={() => downloadPdfAsBlob(url)}
              className={styles.retryButton}
            >
              다시 시도
            </button>
            <button 
              onClick={() => window.open(url, '_blank')}
              className={styles.openButton}
            >
              새 창에서 열기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PDF 표시
  return (
    <div className={`${styles.pdfViewer} ${isFullscreen ? styles.fullscreenMode : ''}`} ref={containerRef}>
      <div className={styles.pdfContainer}>
        {showTwoPages ? (
          <div className={styles.twoPageContainer}>
            <div className={styles.pdfCanvasContainer}>
              <canvas 
                ref={canvasRef}
                className={styles.pdfCanvas}
              />
            </div>
            <div className={styles.pdfCanvasContainer}>
              <canvas 
                ref={canvas2Ref}
                className={styles.pdfCanvas}
              />
            </div>
          </div>
        ) : (
          <div className={styles.pdfCanvasContainer}>
            <canvas 
              ref={canvasRef}
              className={styles.pdfCanvas}
            />
          </div>
        )}
        
        {/* 오버레이 페이지 컨트롤 */}
        {showControls && (
          <div className={styles.overlayControls}>
            <button 
              onClick={goToPreviousPage} 
              disabled={currentPage <= 1}
              className={`${styles.navButton} ${styles.prevButton}`}
              title="이전 페이지"
            >
              ‹
            </button>
            <button 
              onClick={goToNextPage} 
              disabled={currentPage >= totalPages}
              className={`${styles.navButton} ${styles.nextButton}`}
              title="다음 페이지"
            >
              ›
            </button>
          </div>
        )}
        
        {/* 페이지 정보 */}
        {showControls && (
          <div className={styles.pageInfoOverlay}>
            <div className={styles.pageInfo}>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                min={1}
                max={totalPages}
                className={styles.pageInput}
              />
              <span> / {totalPages}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;